import React, { useEffect, useState } from 'react';
import MealPlan from '../components/MealPlan';
import GroceryList from '../components/GroceryList';
import { Spinner, Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MealSteps from './MealSteps';
import ErrorBoundary from '../components/ErrorBoundary';
import DynamicExploreRecipes from '../components/DynamicExploreRecipes';

import { useCSRF } from '../utils/csrfUtils';

const MealPlanPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { pgUser, sessionToken } = useAuth();
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const { fetchWithCSRF, isLoading: csrfLoading } = useCSRF();

    const [mealPlanId, setMealPlanId] = useState(null);
    const [mealPlan, setMealPlan] = useState({ meals: [] });
    const [groceryListId, setGroceryListId] = useState(null);
    const [isCurrentMealPlan, setIsCurrentMealPlan] = useState(null);
    const [appUserId, setAppUserId] = useState(null);
    const [activeView, setActiveView] = useState('meals');
    const [isLoading, setIsLoading] = useState(true);
    const [groceryListRefreshTrigger, setGroceryListRefreshTrigger] = useState(0);
    const [isSetupComplete, setIsSetupComplete] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [mealsBeingGenerated, setMealsBeingGenerated] = useState(false);
    
    // Read mealId from URL and find meal in the meal plan
    const mealIdFromUrl = searchParams.get('mealId');
    const mealPlanIdFromUrl = searchParams.get('mealPlanId');
    const [selectedMeal, setSelectedMeal] = useState(null);
    
    // Find meal in meal plan when mealId is in URL
    useEffect(() => {
        if (!mealIdFromUrl) {
            setSelectedMeal(null);
            return;
        }
        
        console.log('Looking for mealId:', mealIdFromUrl);
        console.log('mealPlan:', mealPlan);
        console.log('mealPlan.meals:', mealPlan.meals);
        
        // Wait for meal plan to load and find the meal
        if (mealPlan.meals && Array.isArray(mealPlan.meals)) {
            console.log('Available meal IDs:', mealPlan.meals.map(m => ({ id: m.id, name: m.recipe_name })));
            const foundMeal = mealPlan.meals.find(m => String(m.id) === mealIdFromUrl);
            if (foundMeal) {
                console.log('Found meal:', foundMeal);
                setSelectedMeal(foundMeal);
            } else {
                console.log('Meal not found in current meal plan');
                setSelectedMeal(null);
            }
        } else {
            console.log('mealPlan.meals is not loaded yet');
        }
    }, [mealIdFromUrl, mealPlan.meals]);
    const [todaysBrowserDate, setTodaysBrowserDate] = useState(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    });
    const [hasNextMealPlan, setHasNextMealPlan] = useState(false);
    const [hasPreviousMealPlan, setHasPreviousMealPlan] = useState(false);
    const [showRecipeSearchOverlay, setShowRecipeSearchOverlay] = useState(false);
    const [mealPlanRefreshTrigger, setMealPlanRefreshTrigger] = useState(0);

    // Handle auth loading state
    useEffect(() => {
        console.log('sessionToken: ', sessionToken);
        if (pgUser === null) {
            setIsAuthLoading(false);
            return;
        }
        setAppUserId(pgUser?.id);
        setIsSetupComplete(pgUser?.setup_complete);
        setHasAccess(pgUser?.has_access);
        console.log('has access: ', pgUser?.has_access);
        setIsAuthLoading(false);
    }, [pgUser]);

    useEffect(() => {
        const fetchMealPlan = async () => {
        setIsLoading(true);

        // Only proceed if we have a valid appUserId
        if (!appUserId) {
            setIsLoading(false);
            return;
        }

        if (isSetupComplete) {
            // Check if mealPlanId is provided in URL
            if (mealPlanIdFromUrl && mealPlanId !== mealPlanIdFromUrl) {
                console.log("fetching meal plan with mealPlanId from URL: ", mealPlanIdFromUrl);
                setMealPlanId(mealPlanIdFromUrl);
                setIsCurrentMealPlan(false);
                fetchWithCSRF(process.env.REACT_APP_POSTGRES_API_ENDPOINT + '/meal-plan/' + mealPlanIdFromUrl + '/app_user/' + appUserId, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                })
                .then((response) => response.json())
                .then((data) => {
                    setMealPlan(data);
                    setMealPlanId(data.id);
                    setGroceryListId(data.grocery_list_id);
                    checkAdjacentMealPlans(data.id);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching meal plan:', error);
                    setIsLoading(false);
                });
            } else if (isCurrentMealPlan || mealPlanId == null) {
                console.log("fetching meal plan with todaysBrowserDate: ", todaysBrowserDate);
                fetchWithCSRF(process.env.REACT_APP_POSTGRES_API_ENDPOINT + '/meal-plan/date/' + todaysBrowserDate + '/app_user/' + appUserId, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                })
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.json();
                    })
                    .then((data) => {
                        if (!data || !data[0]) {
                            console.error('No meal plan returned:', data);
                            console.log('setting mealsBeingGenerated to true');
                            setMealsBeingGenerated(true);
                            setIsLoading(false);
                            return;
                        }
                        setMealPlan(data[0]);
                        setMealPlanId(data[0].id);
                        setGroceryListId(data[0].grocery_list_id);
                        checkAdjacentMealPlans(data[0].id);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        console.error('Error fetching meal plan:', error);
                        setIsLoading(false);
                    });
            } else {
                if (!mealPlan || !mealPlan.id || mealPlan.id !== mealPlanId) {
                    console.log("inside else statement, fetching meal plan with mealPlanId: ", mealPlanId);
                    fetchWithCSRF(process.env.REACT_APP_POSTGRES_API_ENDPOINT + '/meal-plan/' + mealPlanId + '/app_user/' + appUserId, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${sessionToken}`
                        },
                    })
                    .then((response) => response.json())
                    .then((data) => {
                        setMealPlan(data);
                        setMealPlanId(data.id);
                        setGroceryListId(data.grocery_list_id);
                        checkAdjacentMealPlans(data.id);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        console.error('Error fetching meal plan:', error);
                        setIsLoading(false);
                    });
                } else {
                    setIsLoading(false);
                }
            }
        } else if (isSetupComplete === false) {
            navigate('/setup');
        }
    }
    fetchMealPlan();
}, [appUserId, isCurrentMealPlan, mealPlanId, isSetupComplete, navigate, todaysBrowserDate, mealPlanRefreshTrigger, mealPlanIdFromUrl, sessionToken, fetchWithCSRF]);

    const checkAdjacentMealPlans = async (currentMealPlanId) => {
        try {

            // Check for next meal plan
            const nextResponse = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/meal-plan/next/${currentMealPlanId}/app_user/${appUserId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
            });
            if (nextResponse.status === 200 && nextResponse.headers.get("content-length") !== "0") {
                const nextData = await nextResponse.json();
                setHasNextMealPlan(!!nextData && Object.keys(nextData).length > 0);
            } else {
                setHasNextMealPlan(false);
            }

            // Check for previous meal plan
            const prevResponse = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/meal-plan/previous/${currentMealPlanId}/app_user/${appUserId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
            });
            if (prevResponse.status === 200 && prevResponse.headers.get("content-length") !== "0") {
                const prevData = await prevResponse.json();
                setHasPreviousMealPlan(!!prevData && Object.keys(prevData).length > 0);
            } else {
                setHasPreviousMealPlan(false);
            }
        } catch (error) {
            console.error('Error checking adjacent meal plans:', error);
            setHasNextMealPlan(false);
            setHasPreviousMealPlan(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        // Extract just the date part (YYYY-MM-DD) to avoid timezone conversion issues
        const datePart = dateString.split('T')[0];
        const date = new Date(datePart + 'T12:00:00'); // Add noon time to avoid any edge cases
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric'
        });
    };

    const handleMealPlanClick = async (mealPlanId, nextOrPrevious) => {
        setIsLoading(true);
        fetchWithCSRF(process.env.REACT_APP_POSTGRES_API_ENDPOINT + '/meal-plan/' + nextOrPrevious + '/' + mealPlanId + '/app_user/' + appUserId, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (!data) {
                    console.log(`No ${nextOrPrevious} meal plan available`);
                    if (nextOrPrevious === 'next') {
                        setHasNextMealPlan(false);
                    } else {
                        setHasPreviousMealPlan(false);
                    }
                    setIsLoading(false);
                    return;
                }
                console.log("new meal plan id: " + data.id);
                // Set all state updates together to prevent intermediate undefined values
                setMealPlanId(data.id);
                setMealPlan(data);
                setGroceryListId(data.grocery_list_id);
                setGroceryListRefreshTrigger(prev => prev + 1);
                checkAdjacentMealPlans(data.id);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching meal plan:', error);
                setIsLoading(false);
            });
    };

    // Check if meal plan is in the past
    const isMealPlanInPast = () => {
        if (!mealPlan || !mealPlan.end_date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        // Parse date string as local date by adding T12:00:00 to avoid timezone shift
        const endDateStr = mealPlan.end_date.split('T')[0];
        const endDate = new Date(endDateStr + 'T12:00:00');
        endDate.setHours(0, 0, 0, 0); // Reset time to start of day
        return endDate < today;
    };

    // Callback when a meal is added from the recipe search overlay
    const handleMealAddedFromSearch = () => {
        setMealPlanRefreshTrigger(prev => prev + 1);
        setShowRecipeSearchOverlay(false);
    };

    // Check if current date is within meal plan dates and return appropriate badge text
    const getWeekBadge = () => {
        if (!mealPlan || !mealPlan.start_date || !mealPlan.end_date) return null;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        
        // Parse date strings as local dates by adding T12:00:00 to avoid timezone shift
        const startDateStr = mealPlan.start_date.split('T')[0];
        const endDateStr = mealPlan.end_date.split('T')[0];
        
        const startDate = new Date(startDateStr + 'T12:00:00');
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(endDateStr + 'T12:00:00');
        endDate.setHours(0, 0, 0, 0);
        
        // Check if today is within the meal plan date range
        if (today >= startDate && today <= endDate) {
            return "This week";
        }
        
        // Check if meal plan is next week (within 7 days from today)
        const nextWeekDate = new Date(today);
        nextWeekDate.setDate(today.getDate() + 7);
        nextWeekDate.setHours(0, 0, 0, 0);
        
        if (startDate >= today && startDate <= nextWeekDate) {
            return "Next week";
        }
        
        return null;
    };

    // Callback for when meal card is clicked - meal cards will also set URL param
    const handleStartCooking = (meal) => {
        // The MealCard components handle setting the URL parameter
        // This callback can be used for any additional logic if needed
    };

    // If mealId is in URL but meal hasn't been found yet, show loading
    if (mealIdFromUrl && !selectedMeal && isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                    <span className="visually-hidden">Loading meal...</span>
                </div>
            </div>
        );
    }

    if (selectedMeal) {
        return (
            <ErrorBoundary fallbackMessage="Unable to load meal steps. Please try again later.">
                <div className="meal-steps-container">
                    <MealSteps 
                        meal={selectedMeal} 
                        onBack={() => {
                            // Use browser's back button functionality
                            window.history.back();
                        }} 
                    />
                </div>
            </ErrorBoundary>
        );
    }

    if (isLoading) {
        return (
            <div>
                {mealsBeingGenerated && (
                    <div className="alert alert-warning" role="alert">
                        <strong>Meals are being generated...</strong> Please check back in a few minutes.
                    </div>
                )}
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                </div>
            </div>
        );
    }

    return (
        <ErrorBoundary fallbackMessage="Unable to load meal plan. Please try again later.">
            <div className="container">
                {isAuthLoading ? (
                    <div className="text-center my-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : !pgUser ? (
                    <div className="alert alert-warning">
                        <p className="h5 mb-3">Unable to connect to the server. Please try again later or send a message to <a href="mailto:support@getautochef.com">support@getautochef.com</a></p>
                        <Button onClick={() => window.location.reload()}>Retry</Button>
                    </div>
                ) : (
                    <>
                        {mealsBeingGenerated && hasAccess && (
                            <div className="" role="alert">
                                <h3>Welcome to AutoChef!</h3>
                                <strong>We're cooking up your meal plan...</strong> Please check back in a few minutes.
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '10vh' }}>
                                    <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                                        <span className="visually-hidden">MealPlanPageLoading...</span>
                                    </Spinner>
                                </div>
                            </div>
                        )}

                        {!hasAccess && (
                            <div className="d-flex flex-column justify-content-center align-items-center text-center" role="alert">
                                <h3 className="mb-4 mt-4">Your meal plan is waiting for you! Complete your account to get started.</h3>
                                <button className="btn btn-primary" onClick={() => navigate('/pricing')}>Get meal plan</button>
                            </div>
                        )}

                        {(!pgUser.setup_complete) && (
                            <div className="alert alert-warning">
                                <p className="h5 mb-3">Complete meal preferences to start planning meals!</p>
                                <Button onClick={() => navigate('/setup')}>Complete Setup</Button>
                            </div>
                        )}

                        {isSetupComplete && !mealsBeingGenerated && hasAccess && (
                            <div>
                                {/* Search Bar */}
                                {/* <div className="mb-3 d-flex justify-content-center">
                                    <div 
                                        className="recipe-search-bar-trigger"
                                        onClick={() => setShowRecipeSearchOverlay(true)}
                                        style={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            padding: '10px 15px',
                                            border: '1px solid #ced4da',
                                            borderRadius: '0.25rem',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}
                                    >
                                        <svg 
                                            width="20" 
                                            height="20" 
                                            viewBox="0 0 16 16" 
                                            fill="currentColor"
                                            style={{ opacity: 0.5 }}
                                        >
                                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                                        </svg>
                                        <span style={{ color: '#6c757d' }}>Find a recipe...</span>
                                    </div>
                                </div> */}

                                <div className="meal-plan-date-navigation d-flex justify-content-center align-items-center mb-3">
                                    {hasPreviousMealPlan && (
                                        <button className="btn btn-link p-0 border-0" style={{ background: 'none' }} onClick={() => handleMealPlanClick(mealPlanId, 'previous')}>
                                            <img src="/icons/chevron-left.svg" alt="" />
                                        </button>
                                    )}
                                    {!hasPreviousMealPlan && (
                                        <div>
                                            <img src="/icons/chevron-left.svg" alt="" style={{ visibility: 'hidden' }} />
                                        </div>
                                    )}
                                    <div className="d-flex flex-column align-items-center">
                                        <h4 className="mx-3 mb-0">
                                            {formatDate(mealPlan.start_date)} - {formatDate(mealPlan.end_date)}
                                        </h4>
                                        {getWeekBadge() && (
                                            <span className="badge mt-1" style={{ fontSize: '1rem', backgroundColor: '#52cd92', color: 'white', borderRadius: '4px' }}>
                                                {getWeekBadge()}
                                            </span>
                                        )}
                                    </div>
                                    {hasNextMealPlan && (
                                        <button className="btn btn-link p-0 border-0" style={{ background: 'none' }} onClick={() => handleMealPlanClick(mealPlanId, 'next')}>
                                            <img src="/icons/chevron-right.svg" alt="" />
                                        </button>
                                    )}
                                    {!hasNextMealPlan && (
                                        <div>
                                            <img src="/icons/chevron-right.svg" alt="" style={{ visibility: 'hidden' }} />
                                        </div>
                                    )}
                                </div>
                                {/* <div className="d-flex flex-column align-items-center mb-4">
                                    {hasAccess && (
                                        <div className="d-flex gap-4 mb-0 align-items-center">
                                            <h4 
                                                onClick={() => setActiveView('meals')}
                                                className={`view-selector ${activeView === 'meals' ? 'active' : ''}`}
                                                style={{cursor: 'pointer'}}
                                            >
                                                Meals
                                            </h4>
                                            <div className="view-separator">|</div>
                                            <h4 
                                                onClick={() => setActiveView('grocery')}
                                                className={`view-selector ${activeView === 'grocery' ? 'active' : ''}`}
                                                style={{cursor: 'pointer'}}
                                            >
                                                Grocery List
                                            </h4>
                                        </div>
                                    )}
                                </div> */}
                                {hasAccess && activeView === 'meals' && <MealPlan mealPlanId={mealPlanId} onStartCooking={handleStartCooking} isInPast={isMealPlanInPast()} externalRefreshTrigger={mealPlanRefreshTrigger} />}
                                {hasAccess && activeView === 'grocery' && (
                                    <div className="text-center mt-3">
                                        <GroceryList 
                                            groceryListId={groceryListId} 
                                            refreshTrigger={groceryListRefreshTrigger}
                                            isInPast={isMealPlanInPast()}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Recipe Search Overlay */}
                {showRecipeSearchOverlay && (
                    <>
                        <div 
                            className="overlay-backdrop"
                            onClick={() => setShowRecipeSearchOverlay(false)}
                        />
                        <div className="overlay-content">
                            <div className="d-flex justify-content-end mb-3">
                                <button 
                                    className="btn-close" 
                                    onClick={() => setShowRecipeSearchOverlay(false)}
                                    aria-label="Close"
                                />
                            </div>
                            <DynamicExploreRecipes autoFocusSearch={true} onMealAdded={handleMealAddedFromSearch} />
                        </div>
                    </>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default MealPlanPage;